import { useEffect, useRef, useState } from "react";
import { postJson } from "../api";
import { describeError, t } from "../i18n";

/**
 * The link you give to other people.
 *
 * Distinct from the handoff dialog on purpose: that one moves your own view to
 * another window and burns after a single use. This one is a standing key to
 * the room, so the wording leans on that -- it is capped, it expires, and it
 * can be revoked from here.
 */

interface Props {
  session: string;
  instanceId: string;
  onClose: () => void;
}

interface Invite {
  code: string;
  url: string;
  expiresInMs: number;
  uses: number;
  maxUses: number;
}

export function InviteDialog({ session, instanceId, onClose }: Props) {
  const [invite, setInvite] = useState<Invite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");
  const [revoked, setRevoked] = useState(false);
  const urlField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    postJson<Invite>("/api/invite", { session, instanceId })
      .then((result) => !cancelled && setInvite(result))
      .catch((failure: unknown) => {
        if (!cancelled) setError(describeError(failure, "invite.failed"));
      });
    return () => {
      cancelled = true;
    };
  }, [session, instanceId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** Same three-step fallback as the handoff dialog; the iframe often refuses
   *  the Clipboard API outright, and a button that silently does nothing is
   *  worse than one that says it could not. */
  const copy = async () => {
    if (!invite) return;
    let ok = false;
    if (navigator.clipboard?.writeText) {
      ok = await navigator.clipboard
        .writeText(invite.url)
        .then(() => true)
        .catch(() => false);
    }
    if (!ok && urlField.current) {
      try {
        urlField.current.focus();
        urlField.current.select();
        urlField.current.setSelectionRange(0, invite.url.length);
        ok = document.execCommand("copy");
      } catch {
        ok = false;
      }
    }
    setCopyState(ok ? "done" : "failed");
    if (ok) window.setTimeout(() => setCopyState("idle"), 1800);
  };

  const revoke = async () => {
    if (!invite) return;
    try {
      await postJson("/api/invite/revoke", { session, code: invite.code });
      setRevoked(true);
    } catch (failure) {
      setError(describeError(failure, "invite.revokeFailed"));
    }
  };

  const hours = invite ? Math.floor(invite.expiresInMs / 3_600_000) : 0;
  const minutes = invite ? Math.round((invite.expiresInMs % 3_600_000) / 60_000) : 0;

  return (
    <div className="modal" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal__panel" role="dialog" aria-modal="true" aria-label={t("invite.title")}>
        <header className="modal__head">
          <strong>{t("invite.title")}</strong>
          <button type="button" className="modal__close" onClick={onClose} aria-label={t("pin.close")}>
            ✕
          </button>
        </header>

        {revoked ? (
          <p className="feedback__done">{t("invite.revoked")}</p>
        ) : (
          <>
            <p className="handoff__lede">
              {t("invite.lede")}
            </p>

            {error && <p className="feedback__error">{error}</p>}

            {invite && (
              <>
                <input
                  ref={urlField}
                  className="handoff__url"
                  value={invite.url}
                  readOnly
                  spellCheck={false}
                  aria-label={t("invite.title")}
                  onFocus={(event) => event.currentTarget.select()}
                />

                <button type="button" className="handoff__open" onClick={() => void copy()}>
                  {copyState === "done"
                    ? t("handoff.copied")
                    : copyState === "failed"
                      ? t("handoff.copyFailed")
                      : t("invite.copy")}
                </button>

                <p className="invite__warn">{t("invite.warn")}</p>

                <p className="handoff__note">
                  {t("invite.budget", {
                    left: invite.maxUses - invite.uses,
                    time: hours > 0 ? t("time.hoursMinutes", { h: hours, m: minutes }) : t("time.minutes", { n: minutes }),
                    used: invite.uses,
                    max: invite.maxUses,
                  })}
                </p>

                <button type="button" className="invite__revoke" onClick={() => void revoke()}>
                  {t("invite.revoke")}
                </button>
              </>
            )}

            {!invite && !error && <p className="handoff__note">{t("invite.creating")}</p>}
          </>
        )}
      </div>
    </div>
  );
}
